// The collections, groups and tags API.
//
// Same shape as the workspaces client next door: credentials on every call
// (the session is a cookie the page cannot read), and the SERVER'S message on
// every failure — these routes answer with sentences written for the person
// reading them ("slides are still being processed", "a slide was added while
// the collection was being deleted"), and a client that paraphrases loses the
// only explanation of why something was refused.
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';

/** Server visibility values. The community shell maps them to Private / Shared / Public. */
export type ServerVisibility = 'workspace' | 'org' | 'unlisted' | 'public';

/** A generic named container inside one workspace. */
export interface Collection {
  public_id: string;
  name: string;
  /** Rich text (sanitized HTML) in the community shell; plain text on the enterprise page. */
  description: string | null;
  visibility: ServerVisibility;
  created_by: string | null;
  /** Display name of created_by, resolved by the server. */
  author: string | null;
  created_utc: string;
  modified_utc: string;
  /** How many studies are filed here, across every group and the ungrouped band. */
  studies: number;
  groups: number;
  images: number;
  /** The first three studies in placement order, for the mosaic, by public id (`stu_…`). */
  covers: string[];
  like_count: number;
  comment_count: number;
  liked: boolean;
}

export interface CollectionPage {
  collections: Collection[];
  /**
   * An admin or editor of the workspace this page lists: may create, delete
   * and organise here. Viewers may not.
   *
   * On the PAGE, not on a row, because the list is one workspace — the server
   * resolves exactly one from `?workspace=` or the caller's default, so a
   * per-row flag would be the same boolean repeated.
   *
   * Optional, and every reader must treat a missing value as FALSE. A response
   * from a server that predates it is not evidence of permission.
   */
  editable?: boolean;
  total: number;
  /** How many of the filtered set the caller has saved (the Saved tab count). */
  saved: number;
  page: number;
  limit: number;
}

/**
 * The optional second level inside a collection.
 *
 * Called a Group everywhere a person can see, and StudyGroup everywhere in code
 * — `group` already means an annotation grouping key, the keyboard-shortcut
 * groups and the settings sections, and the viewer has its own <Group>.
 */
export interface StudyGroup {
  public_id: string;
  name: string;
  description: string | null;
  ordinal: string;
}

export interface PlacedStudy {
  /**
   * The study's id in this API (`stu_…`). A StudyInstanceUID is unique only
   * inside a workspace, so every app route names a study by this instead.
   */
  public_id: string;
  /** Still returned to members: the enterprise study page is reached by UID. */
  study_instance_uid: string;
  ordinal: string | null;
  /** null means the ungrouped band, which is a real place and the common one. */
  group_public_id: string | null;
  /** The community study fields, mirrored from the object at ingest. */
  study_id: string | null;
  description: string | null;
  diagnosis: string | null;
  /** Effective (the wider of its own and its collection's) and its own. */
  visibility: ServerVisibility;
  visibility_own: ServerVisibility;
  like_count: number;
  comment_count: number;
  liked: boolean;
  images: number;
  /** How many of those images are still converting or awaiting their details. */
  unpublished: number;
  created_utc: string;
}

export interface CollectionDetail {
  publicId: string;
  name: string;
  description: string | null;
  visibility: ServerVisibility;
  /**
   * An admin or editor of its workspace: may rename it, describe it, file and
   * delete studies in it, change its visibility and its link, and delete it.
   * Viewers may not.
   *
   * Optional, and a missing value MUST read as false — an older server, a
   * share visitor's payload or a half-built cache must never be mistaken for
   * permission. Every use of it in the pages reads `!editable` for that reason.
   */
  editable?: boolean;
  author: string | null;
  created_by: string | null;
  created_utc: string;
  modified_utc: string;
  like_count: number;
  comment_count: number;
  liked: boolean;
  groups: StudyGroup[];
  studies: PlacedStudy[];
  total: number;
  limit: number;
  offset: number;
}

export interface Tag {
  public_id: string;
  name: string;
  color: string | null;
  studies: number;
}

/** The server's sentence, and its `status` where there was an answer to read
 *  one from. Callers that must tell "gone" from "broken" — the upload pages —
 *  ask for 404. */
export class ApiError extends HttpError {}

/** A request of the app API (apiRoot, the session cookie); a refusal is an ApiError with the server's sentence and hint. */
export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${getConfig().apiRoot}${path}`, {
      credentials: 'include',
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as { error?: string; hint?: string };
  if (!res.ok) {
    // A 409 here carries a hint that IS the instruction — what is in the way and
    // what to do about it. Dropping it would leave the user with a refusal and
    // no way forward.
    throw new ApiError([body.error, body.hint].filter(Boolean).join(' — ') || `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

/** Ids are server-generated and cannot carry a slash today; encoded anyway,
 *  for the same reason the workspaces client does it. */
const seg = encodeURIComponent;

const ws = (workspace?: string) => (workspace ? `?workspace=${seg(workspace)}` : '');

export interface ListCollectionsQuery {
  workspace?: string;
  q?: string;
  tab?: 'all' | 'saved';
  visibility?: 'all' | ServerVisibility;
  page?: number;
  limit?: number;
  sort?: 'recent' | 'name';
}

export const listCollections = (query: string | ListCollectionsQuery = {}): Promise<CollectionPage> => {
  const o = typeof query === 'string' ? { workspace: query } : query;
  const q = new URLSearchParams();
  if (o.workspace) q.set('workspace', o.workspace);
  if (o.q) q.set('q', o.q);
  if (o.tab && o.tab !== 'all') q.set('tab', o.tab);
  if (o.visibility && o.visibility !== 'all') q.set('visibility', o.visibility);
  if (o.page && o.page > 1) q.set('page', String(o.page));
  if (o.limit) q.set('limit', String(o.limit));
  if (o.sort) q.set('sort', o.sort);
  const s = q.toString();
  return call(`/collections${s ? `?${s}` : ''}`);
};

export interface CollectionTarget {
  public_id: string;
  name: string;
  groups: Array<{ public_id: string; name: string }>;
}

/** Every collection and group in the workspace, names only: menu targets. */
export const listCollectionTargets = (workspace?: string): Promise<{ collections: CollectionTarget[] }> =>
  call(`/collections/targets${ws(workspace)}`);

/** What copying a whole collection made: the new collection, and what went into it. */
export interface CollectionCopy {
  collection: { public_id: string; name: string };
  studies: number;
  groups: number;
  copying: number;
}

/**
 * Copy a whole collection into another workspace the caller administers: a new
 * collection there with its groups and a copy of every study — new ids, the
 * same details and images, and none of the original's comments, likes, share
 * links or uploaders. It starts private, whatever the original is.
 */
export const copyCollectionTo = (collectionId: string, workspace: string): Promise<CollectionCopy> =>
  call(`/collections/${seg(collectionId)}/copy`, { method: 'POST', body: JSON.stringify({ workspace }) });

/** One workspace the caller may write, with its collections: a section of a Copy menu. */
export interface WritableWorkspaceTargets {
  public_id: string;
  name: string;
  is_default: boolean;
  /** The caller administers it. Slides are copied between workspaces only by someone who administers both. */
  is_admin?: boolean;
  collections: CollectionTarget[];
}

/**
 * Every collection and group in every workspace the caller may WRITE: where a
 * Move or Duplicate may land. A workspace they only view is absent, since the
 * server would refuse anything filed there.
 */
export const listWritableTargets = (): Promise<{ workspaces: WritableWorkspaceTargets[] }> =>
  call('/collections/targets?scope=writable');

/**
 * The writable listing as a menu shows it: the workspace the person is working
 * in first, the others in the server's order, and none without a collection
 * (there is nothing in it to pick). Another workspace only when the person
 * administers both it and the current one: nobody else may copy slides between
 * them (the server refuses), and nothing may be moved between them at all.
 * `labelled` says whether the sections need their workspace names — not when
 * the only one is the current workspace, which keeps the single-workspace menu
 * looking as it always has.
 */
export function orderTargetWorkspaces(
  workspaces: readonly WritableWorkspaceTargets[],
  current: string | undefined,
): { workspaces: WritableWorkspaceTargets[]; labelled: boolean } {
  const withCollections = workspaces.filter((w) => w.collections.length > 0);
  const across = workspaces.some((w) => w.public_id === current && w.is_admin === true);
  const ordered = [
    ...withCollections.filter((w) => w.public_id === current),
    ...(across ? withCollections.filter((w) => w.public_id !== current && w.is_admin === true) : []),
  ];
  return { workspaces: ordered, labelled: ordered.some((w) => w.public_id !== current) };
}

/** The writable workspace a collection is in, from the same listing. */
export const workspaceOfCollection = (
  workspaces: readonly WritableWorkspaceTargets[],
  collectionId: string,
): WritableWorkspaceTargets | undefined => workspaces.find((w) => w.collections.some((c) => c.public_id === collectionId));

export const getCollection = (id: string, limit?: number, offset?: number): Promise<CollectionDetail> => {
  const q = new URLSearchParams();
  if (limit !== undefined) q.set('limit', String(limit));
  if (offset) q.set('offset', String(offset));
  const s = q.toString();
  return call(`/collections/${seg(id)}${s ? `?${s}` : ''}`);
};

export const createCollection = (name: string, workspace?: string, description?: string): Promise<Collection> =>
  call('/collections', { method: 'POST', body: JSON.stringify({ name, workspace, description }) });

export const renameCollection = (id: string, name: string): Promise<Collection> =>
  call(`/collections/${seg(id)}`, { method: 'PATCH', body: JSON.stringify({ name }) });

/** Rich text; the server sanitizes to the editor's own vocabulary. */
export const describeCollection = (id: string, description: string): Promise<Collection> =>
  call(`/collections/${seg(id)}`, { method: 'PATCH', body: JSON.stringify({ description }) });

export const setCollectionVisibility = (id: string, visibility: ServerVisibility): Promise<Collection> =>
  call(`/collections/${seg(id)}`, { method: 'PATCH', body: JSON.stringify({ visibility }) });

/** A study's OWN visibility; the effective value comes back as `visibility`. */
export const setStudyVisibility = (
  studyId: string,
  visibility: ServerVisibility,
): Promise<{ public_id: string; visibility: ServerVisibility; visibility_own: ServerVisibility }> =>
  call(`/studies/${seg(studyId)}`, { method: 'PATCH', body: JSON.stringify({ visibility }) });

/**
 * A study's UID and workspace from its public id — the reverse of lookupStudy,
 * for the pages that are handed a public id (the pipeline's status) but still
 * address the study by UID (the enterprise study page, a QIDO probe). Reads the
 * study head, which answers members and, for a published Public study, anyone
 * signed in.
 */
export const getStudyRef = <T extends StudyRef = StudyRef>(studyId: string): Promise<T> =>
  call(`/studies/${seg(studyId)}`);

/** What every caller of the study head reads; the details editor reads more of the same answer. */
export interface StudyRef {
  public_id: string;
  study_instance_uid: string;
  workspace: string;
  editable: boolean;
}

/**
 * A study's public id from its UID, for the pages that only have the UID: the
 * enterprise worklist lists QIDO rows. A UID names one study PER WORKSPACE, so
 * the workspace is part of the question — the one named, else the caller's
 * default, which is also what the root DICOMweb mount reads. Members only.
 */
export const lookupStudy = (studyUid: string, workspace?: string): Promise<{ public_id: string; workspace: string }> => {
  const q = new URLSearchParams({ uid: studyUid });
  if (workspace) q.set('workspace', workspace);
  return call(`/studies/lookup?${q}`);
};

export const like = (kind: 'collection' | 'study', id: string): Promise<void> =>
  call(`/likes/${kind}/${seg(id)}`, { method: 'PUT' });

export const unlike = (kind: 'collection' | 'study', id: string): Promise<void> =>
  call(`/likes/${kind}/${seg(id)}`, { method: 'DELETE' });

/** What a destructive delete removed. Shown back to the user, because "gone"
 *  is easier to trust with a number attached. */
export interface PurgeResult {
  deleted: true;
  /** How many slides were marked deleted — and they are invisible already. */
  studies: number;
  /**
   * The background jobs queued to destroy them.
   *
   * There are no instance or object counts here, and there cannot be: the
   * teardown has not run yet when this returns. The server used to report them
   * because it did the work inline; declaring them now would be a type that
   * lies about the wire.
   */
  jobs: string[];
}

/**
 * Delete a collection AND THE SLIDES IN IT. Not undoable.
 *
 * There is no archive state and no empty-first rule: the collection goes, and
 * so does everything filed under it — instances, stored objects, annotations.
 * To dissolve a collection without losing slides, move them out first.
 */
export const deleteCollection = (id: string): Promise<PurgeResult> =>
  call(`/collections/${seg(id)}`, { method: 'DELETE' });

/** Delete a study outright. Distinct from unfiling, which keeps the slide. */
export const deleteStudy = (studyId: string): Promise<PurgeResult> =>
  call(`/studies/${seg(studyId)}`, { method: 'DELETE' });

export const createGroup = (collectionId: string, name: string, description?: string): Promise<StudyGroup> =>
  call(`/collections/${seg(collectionId)}/groups`, { method: 'POST', body: JSON.stringify({ name, description }) });

export const renameGroup = (collectionId: string, groupId: string, name: string): Promise<StudyGroup> =>
  call(`/collections/${seg(collectionId)}/groups/${seg(groupId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });

export const describeGroup = (collectionId: string, groupId: string, description: string): Promise<StudyGroup> =>
  call(`/collections/${seg(collectionId)}/groups/${seg(groupId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ description }),
  });

/**
 * Delete a group.
 *
 * By default its studies STAY, dropping to the ungrouped band (2026-09-09: a
 * group is a band on a page, and the enterprise worklist offers "Move contents
 * out" beside the delete). Pass `withStudies` for the destructive version the
 * community page asks for by name — the slides go too, and it is not undoable.
 */
export const deleteGroup = (
  collectionId: string,
  groupId: string,
  withStudies?: boolean,
): Promise<{ deleted: true; ungrouped: number }> =>
  call(`/collections/${seg(collectionId)}/groups/${seg(groupId)}${withStudies ? '?studies=delete' : ''}`, { method: 'DELETE' });

/** What a duplicate made. */
export interface DuplicateResult {
  studies: number;
  groups: number;
  /**
   * Images whose bytes are still being copied into another workspace. The
   * copied studies exist already; each of these images appears in them only
   * once its copy finishes, usually seconds to minutes later.
   */
  copying: number;
}

/**
 * Duplicate studies and/or groups into a collection (groups only into a
 * collection), in this workspace or in another one the caller ADMINISTERS (as
 * they do this one). Inside one workspace the copies share their objects; into
 * another, the images are copied in the background (`copying`).
 */
export const duplicateInto = (v: { studyIds?: string[]; groupIds?: string[]; collectionId: string; studyGroupId?: string }): Promise<DuplicateResult> =>
  call('/studies/duplicate', { method: 'POST', body: JSON.stringify(v) });

/**
 * Move a group, with its studies, to another collection of the same workspace.
 * Nothing moves between workspaces — a copy goes there instead.
 */
export const moveGroupToCollection = (collectionId: string, groupId: string, targetCollectionId: string): Promise<{ moved: number }> =>
  call(`/collections/${seg(collectionId)}/groups/${seg(groupId)}/collection`, {
    method: 'PUT',
    body: JSON.stringify({ collectionId: targetCollectionId }),
  });

/** Reorder by naming NEIGHBOURS, never by sending the whole list — a full list
 *  forces an N-row rewrite server-side and races when two people drag at once. */
export const moveGroup = (
  collectionId: string,
  groupId: string,
  neighbours: { afterId?: string; beforeId?: string },
): Promise<StudyGroup> =>
  call(`/collections/${seg(collectionId)}/groups/${seg(groupId)}/position`, {
    method: 'PUT',
    body: JSON.stringify(neighbours),
  });

/**
 * File a study. SINGLE HOME — this replaces its placement rather than adding.
 *
 * An omitted studyGroupId means "in the collection, ungrouped", which is a
 * legal and common state, not an error. Within one workspace: a collection of
 * another is refused, since slides are copied there rather than moved.
 */
export const fileStudy = (
  studyId: string,
  collectionId: string,
  studyGroupId?: string,
): Promise<void> =>
  call(`/studies/${seg(studyId)}/collection`, {
    method: 'PUT',
    body: JSON.stringify({ collectionId, studyGroupId }),
  });

export const unfileStudy = (studyId: string): Promise<void> =>
  call(`/studies/${seg(studyId)}/collection`, { method: 'DELETE' });

/** Neighbours by public id too: the server reads them from the study's own band. */
export const moveStudy = (
  studyId: string,
  neighbours: { afterId?: string; beforeId?: string },
): Promise<void> =>
  call(`/studies/${seg(studyId)}/position`, { method: 'PUT', body: JSON.stringify(neighbours) });

/**
 * Bulk move — the non-destructive way to empty a container before deleting it.
 * The studies land in the order given: at the end of the band, or as one block
 * after / before `position`'s neighbour (a study of that band) — drag and drop.
 */
export const moveStudies = (
  studyIds: string[],
  collectionId: string | null,
  studyGroupId?: string,
  position?: { afterId?: string; beforeId?: string },
): Promise<{ moved: number }> =>
  call('/studies/move', {
    method: 'POST',
    body: JSON.stringify({ studyIds, collectionId, studyGroupId, ...position }),
  });

export const listTags = (workspace?: string): Promise<{ tags: Tag[] }> => call(`/tags${ws(workspace)}`);

export const createTag = (name: string, workspace?: string): Promise<Tag> =>
  call('/tags', { method: 'POST', body: JSON.stringify({ name, workspace }) });

export const deleteTag = (id: string): Promise<void> =>
  call(`/tags/${seg(id)}`, { method: 'DELETE' });

export const tagStudy = (studyId: string, tagId: string): Promise<void> =>
  call(`/studies/${seg(studyId)}/tags/${seg(tagId)}`, { method: 'PUT' });

export const untagStudy = (studyId: string, tagId: string): Promise<void> =>
  call(`/studies/${seg(studyId)}/tags/${seg(tagId)}`, { method: 'DELETE' });
